import React, { useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import { useAuthStore } from "../../store/useAuthStore";
import { useSessionStore } from "../../store/useSessionStore";
import {
  confirmCateringDetails,
  confirmTombstoneDetails,
  getAllBodyRemovals,
  getAllCatering,
  getAllCases,
  getAllQuotations,
  getAllTombstones,
  searchCases,
  searchBodyRemovals,
  getBodyRemovalDetails,
  getBodyRemovalHistory,
  getCaseDetails,
  getCaseHistory,
  getCateringHistory,
  getCateringDetails,
  getDriverDetails,
  getDriverHistory,
  getQuotationDetails,
  getTombstoneDetails,
  getVehicleDetails,
  getVehicleHistory,
  updateCateringDetails,
  updateTombstoneDetails,
} from "../../services/dashboard";
import { useBodyRemovalsStore } from "../../store/useBodyRemovalsStore";
import { useCaseStore } from "../../store/useCaseStore";
import { useCateringStore } from "../../store/useCateringStore";
import { useDriverStore } from "../../store/useDriverStore";
import { useQuotationStore } from "../../store/useQuotationStore";
import { useVehicleStore } from "../../store/useVehicleStore";
import { getTombstoneId, useTombstoneStore } from "../../store/useTombstoneStore";
import { canAccessIfasModule } from "../../utils/ifasAccess";
import CasePortal from "./funeral/case-creation/case-portal/CasePortal";
import CaseDetails from "./funeral/case-creation/case-details";
import CateringPortal from "./catering/CateringPortal";
import CateringDetails from "./catering/cateringDetails";
import TombstonePortal from "./tombstone/TombstonePortal";
import TombstoneDetails from "./tombstone/tombstoneDetails";
import RevenuePortal from "./revenue/RevenuePortal";
import CataloguePortal from "./catalogue/CataloguePortal";
import IFASHome from "./ifas/IFASHome";
import IFASReportingHub from "./ifas/reports/IFASReportingHub";
import MortuaryPortal from "./mortuary/MortuaryPortal";
import BodyDispatchPortal from "./body-dispatch/BodyDispatchPortal";
import WarehousePortal from "./warehouse/WarehousePortal";
import FuneralPlanningPortal from "./funeral-planning/FuneralPlanningPortal";
import SpecialServicesPortal from "./special-services/SpecialServicesPortal";
import MemorialsPortal from "./memorials/MemorialsPortal";
import FleetPortal, { type DriversNavArgs } from "./fleet/FleetPortal";
import Drivers from "./fleet-management/driver/Drivers";
import DriverDetails from "./fleet-management/driver-details";
import Vehicles from "./fleet-management/vehicles/vehicles";
import VehicleDetails from "./fleet-management/vehicle-details";
import CreateCase from "../guest-user/case-creation";
import CreateQuotation from "../guest-user/quotation-creation";
import AddDriver from "../guest-user/add-driver";
import AddVehicle from "../guest-user/add-vehicle";
import {
  ADD_DRIVER,
  ADD_VEHICLE,
  BODY_DISPATCH,
  BODY_DISPATCH_DETAILS,
  BODY_REMOVAL,
  CASE_DETAILS,
  CATERING_DETAILS,
  CREATE_CASE,
  CREATE_QUOTATION,
  DRIVERS_DETAILS,
  FLEET,
  FUNERAL_PLANNING,
  HISTORY,
  IFAS_HOME,
  MEMORIALS,
  MORTUARY,
  MORTUARY_DETAILS,
  PAGE_COUNT,
  QUOTATIONS_DETAILS,
  SPECIAL_SERVICES,
  TOMBSTONE_DETAILS,
  VEHICLE_DETAILS,
  WAREHOUSE,
} from "./constants";

/**
 * Everything the IFAS half borrows from the dashboard shell.
 *
 * It is a long list, and deliberately so: it is the honest size of the coupling
 * that used to be invisible because both halves shared one component body. Every
 * entry here is state the shell owns and the shell still renders -- the header
 * controls, the loader, the claims stores IFAS navigation has to clear on its way
 * past. Nothing in this list is IFAS state; that lives in the hook.
 */
export type IfasDashboardShell = {
  effectiveRole: string;
  isIfasUser: boolean;
  isIfasDriver: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  searchText: string;
  setSearchText: (value: string) => void;
  selectedStatus: string | null;
  setSelectedStatus: (value: string | null) => void;
  resetListState: () => void;
  setViewMode: (value: string) => void;
  setShowFilterIcon: (value: boolean) => void;
  setShowSearchInput: (value: boolean) => void;
  setShowCollapse: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setIsEditable: (value: boolean) => void;
  setIsFilterClicked: (value: boolean) => void;
  setClaimType: (value: string) => void;
  setClaimDetails: (value: any) => void;
  setClaimBenefitData: (value: any) => void;
  setFilteredClaims: (value: any) => void;
  setSearchedClaim: (value: any) => void;
  setHistoryData: (value: any) => void;
  setHistoryType: (value: "claim" | "case") => void;
  setHistoryBackView: (value: string) => void;
  setIsChecked: (value: boolean) => void;
  setIsClaimAssigned: (value: boolean) => void;
  setIsOtpVerified: (value: boolean) => void;
  showInfoToast: (message: string) => void;
};

/**
 * The IFAS half of the dashboard: its state, its navigation, and its views.
 *
 * This is a hook rather than an `<IfasDashboard>` component for one structural
 * reason. The Sidebar is rendered by the shell, and roughly twenty of its
 * callbacks are IFAS navigation handlers. A child component owning that state
 * could not hand them back up; a hook returns them, so the shell can wire the
 * Sidebar without knowing what any of them touch.
 *
 * Extracted from features/dashboard/index.tsx (#2089, step C3b). The handler
 * bodies are unchanged from that file -- the shell dependencies are destructured
 * below precisely so that the move needed no edits to them.
 */
export function useIfasDashboard(shell: IfasDashboardShell) {
  const {
    effectiveRole,
    isIfasUser,
    isIfasDriver,
    page,
    pageSize,
    setPage,
    searchText,
    setSearchText,
    selectedStatus,
    setSelectedStatus,
    resetListState,
    setViewMode,
    setShowFilterIcon,
    setShowSearchInput,
    setShowCollapse,
    setIsLoading,
    setIsEditable,
    setIsFilterClicked,
    setClaimType,
    setClaimDetails,
    setClaimBenefitData,
    setFilteredClaims,
    setSearchedClaim,
    setHistoryData,
    setHistoryType,
    setHistoryBackView,
    setIsChecked,
    setIsClaimAssigned,
    setIsOtpVerified,
    showInfoToast,
  } = shell;

  // Read directly rather than taking these as arguments: the catering and
  // tombstone writes need the acting user, and the shell has no other use for
  // it. Same subscriptions the shell makes.
  const { userId } = useAuthStore();
  const { userIdData } = useSessionStore();

  const [casesTotalCount, setCasesTotalCount] = useState(0);
  const [caseSelectedStatus, setCaseSelectedStatus] = useState<string | null>(null);
  const [bodyRemovalSelectedStatus, setBodyRemovalSelectedStatus] = useState<string | null>(null);
  const [bodyRemovalSearchText, setBodyRemovalSearchText] = useState("");
  const [selectedMortuaryRecordId, setSelectedMortuaryRecordId] = useState("");
  const [selectedBodyDispatchNumber, setSelectedBodyDispatchNumber] = useState("");

  const [cateringSearchText, setCateringSearchText] = useState("");
  const [isSavingCatering, setIsSavingCatering] = useState(false);
  const [isConfirmingCatering, setIsConfirmingCatering] = useState(false);
  const [isSavingTombstone, setIsSavingTombstone] = useState(false);
  const [isConfirmingTombstone, setIsConfirmingTombstone] = useState(false);

  const [fleetAssignmentCaseReference, setFleetAssignmentCaseReference] = useState("");
  const [fleetAssignmentPurpose, setFleetAssignmentPurpose] = useState("");
  const [fleetAssignmentRequestReference, setFleetAssignmentRequestReference] = useState("");
  const [fleetAssignmentRole, setFleetAssignmentRole] = useState("");
  const [fleetAssignmentSequence, setFleetAssignmentSequence] = useState<number | null>(null);
  const [fleetFocusDriverNumber, setFleetFocusDriverNumber] = useState("");

  const ensureIfasModuleAccess = (moduleKey: Parameters<typeof canAccessIfasModule>[1]) => {
    if (!isIfasUser) return true;
    if (canAccessIfasModule(effectiveRole, moduleKey)) return true;
    showInfoToast("You do not have access to this IFAS module.");
    setViewMode(isIfasDriver ? "BODY-REMOVAL" : IFAS_HOME);
    return false;
  };

  const onCaseHistoryClicked = async () => {
    const selectedCase = useCaseStore.getState().selectedCase;

    if (!selectedCase?.caseId) {
      showInfoToast("Failed to load case history. Please try again.");
      return;
    }

    setIsLoading(true);
    const res = await getCaseHistory(selectedCase?.caseId);
    if (res.status === 200) {
      setHistoryData(res.data);
      setHistoryType("case");
      setHistoryBackView(CASE_DETAILS);
      setViewMode(HISTORY);
    } else {
      showInfoToast("Failed to load case history. Please try again.");
    }
    setIsLoading(false);
  };

  const onBodyRemovalHistoryClicked = async () => {
    const selectedBodyRemoval =
      useBodyRemovalsStore.getState().selectedBodyRemoval;

    const bodyRemovalId =
      selectedBodyRemoval?.id || selectedBodyRemoval?.caseId || "";

    if (!bodyRemovalId) {
      showInfoToast("Failed to load body removal history. Please try again.");
      return;
    }

    setIsLoading(true);
    const res = await getBodyRemovalHistory(bodyRemovalId);
    if (res.status === 200) {
      setHistoryData(res.data);
      setHistoryType("case");
      setHistoryBackView(BODY_REMOVAL);
      setViewMode(HISTORY);
    } else {
      showInfoToast("Failed to load body removal history. Please try again.");
    }
    setIsLoading(false);
  };

  const onCateringHistoryClicked = async () => {
    const selectedCatering = useCateringStore.getState().selectedCatering;

    if (!selectedCatering?.cateringId) {
      showInfoToast("Failed to load catering history. Please try again.");
      return;
    }

    setIsLoading(true);
    const res = await getCateringHistory(selectedCatering.cateringId);
    if (res.status === 200) {
      setHistoryData(res.data);
      setHistoryType("case");
      setHistoryBackView(CATERING_DETAILS);
      setViewMode(HISTORY);
    } else {
      showInfoToast("Failed to load catering history. Please try again.");
    }
    setIsLoading(false);
  };

  const onDriverHistoryClicked = async () => {
    const selectedDriver = useDriverStore.getState().selectedDriver;

    if (!selectedDriver?.driverID) {
      showInfoToast("Failed to load driver history. Please try again.");
      return;
    }

    setIsLoading(true);
    const res = await getDriverHistory(selectedDriver.driverID);
    if (res.status === 200) {
      setHistoryData(res.data);
      setHistoryType("case");
      setHistoryBackView(DRIVERS_DETAILS);
      setViewMode(HISTORY);
    } else {
      showInfoToast("Failed to load driver history. Please try again.");
    }
    setIsLoading(false);
  };

  const onVehicleHistoryClicked = async () => {
    const selectedVehicle = useVehicleStore.getState().selectedVehicle;

    if (!selectedVehicle?.vehicleID) {
      showInfoToast("Failed to load vehicle history. Please try again.");
      return;
    }

    setIsLoading(true);
    const res = await getVehicleHistory(selectedVehicle.vehicleID);
    if (res.status === 200) {
      setHistoryData(res.data);
      setHistoryType("case");
      setHistoryBackView(VEHICLE_DETAILS);
      setViewMode(HISTORY);
    } else {
      showInfoToast("Failed to load vehicle history. Please try again.");
    }
    setIsLoading(false);
  };

  const handleOnCaseSubmit = () => {
    setClaimDetails(null);
    setIsFilterClicked(false);
    setIsOtpVerified(false);
    setViewMode("CASE-PORTAL");
    setSearchText("");
    setCaseSelectedStatus(null);
  };

  const handleOnQuotationSubmit = () => {
    setClaimDetails(null);
    setIsFilterClicked(false);
    setViewMode("QUOTATIONS");
    //getAllCases(page, PAGE_COUNT);
  };

  const handleOnDriverSubmit = () => {
    setClaimDetails(null);
    setIsFilterClicked(false);
    setViewMode("DRIVERS");
    //getAllCases(page, PAGE_COUNT);
  };
  const handleOnVehicleSubmit = () => {
    setClaimDetails(null);
    setIsFilterClicked(false);
    setViewMode("VEHICLES");
    //getAllCases(page, PAGE_COUNT);
  };

  const resetToCaseList = async () => {
    setIsEditable(false);
    setClaimType("");
    setFilteredClaims(null);
    setIsFilterClicked(false);
    setViewMode("CASE-PORTAL");
    // IFAS Case Portal uses an in-page "Table Filters" panel (not the global header search/filter UI).
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setSearchedClaim(null);
    setSearchText("");
    setCaseSelectedStatus(null);
  };

  const resetToBodyRemovalList = async () => {
    setIsEditable(false);
    setClaimType("");
    setFilteredClaims(null);
    setIsFilterClicked(false);
    setViewMode("BODY-REMOVAL");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setSearchedClaim(null);
    setSearchText("");
    setBodyRemovalSelectedStatus(null);
    setBodyRemovalSearchText("");
  };

  const onCasePortalClicked = async () => {
    if (!ensureIfasModuleAccess("casePortal")) return;
    resetListState();
    setViewMode("CASE-PORTAL");
    //resetData("DEATH");
    // IFAS Case Portal uses an in-page "Table Filters" panel (not the global header search/filter UI).
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setCaseSelectedStatus(null);
  };

  const onBodyRemovalClicked = async () => {
    if (!ensureIfasModuleAccess("bodyRemoval")) return;
    resetListState();
    setViewMode("BODY-REMOVAL");
    //resetData("DEATH");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setBodyRemovalSelectedStatus(null);
    setBodyRemovalSearchText("");
  };

  const onIfasReportsClicked = () => {
    if (!ensureIfasModuleAccess("ifasReports")) return;
    setViewMode("IFAS_REPORTS");
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onFuneralPlanningClicked = async () => {
    if (!ensureIfasModuleAccess("funeralPlanning")) return;
    resetListState();
    setViewMode(FUNERAL_PLANNING);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onSpecialServicesClicked = async () => {
    if (!ensureIfasModuleAccess("specialServices")) return;
    resetListState();
    setViewMode(SPECIAL_SERVICES);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onMemorialsPortalClicked = async () => {
    if (!ensureIfasModuleAccess("memorials")) return;
    resetListState();
    setViewMode(MEMORIALS);
    setShowFilterIcon(false);
    setShowSearchInput(false);
    setSelectedStatus(null);
  };

  const onCateringPortalClicked = async () => {
    if (!ensureIfasModuleAccess("catering")) return;
    resetListState();
    setViewMode("CATERING-PORTAL");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setSelectedStatus(null);
    setCateringSearchText("");
  };

  const onTombstonePortalClicked = async () => {
    if (!ensureIfasModuleAccess("tombstone")) return;
    resetListState();
    setViewMode("TOMBSTONE-PORTAL");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
    setSelectedStatus(null);
  };

  const onQuotationClicked = async () => {
    if (!ensureIfasModuleAccess("quotations")) return;
    resetListState();
    setViewMode("QUOTATIONS");
    // //resetData("DEATH");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
  };

  const onMortuaryClicked = async () => {
    if (!ensureIfasModuleAccess("mortuary")) return;
    resetListState();
    setViewMode(MORTUARY);
    setSelectedMortuaryRecordId("");
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onWarehouseClicked = async () => {
    if (!ensureIfasModuleAccess("warehouse")) return;
    resetListState();
    setViewMode(WAREHOUSE);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onBodyDispatchClicked = async () => {
    if (!ensureIfasModuleAccess("bodyDispatch")) return;
    resetListState();
    setViewMode(BODY_DISPATCH);
    setSelectedBodyDispatchNumber("");
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const handleMortuaryRecordClick = (recordId: string) => {
    setSelectedMortuaryRecordId(recordId);
    setViewMode(MORTUARY_DETAILS);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const resetToMortuaryList = () => {
    setSelectedMortuaryRecordId("");
    setViewMode(MORTUARY);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const handleBodyDispatchClick = (dispatchNumber: string) => {
    setSelectedBodyDispatchNumber(dispatchNumber);
    setViewMode(BODY_DISPATCH_DETAILS);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const resetToBodyDispatchList = () => {
    setSelectedBodyDispatchNumber("");
    setViewMode(BODY_DISPATCH);
    setShowFilterIcon(false);
    setShowSearchInput(false);
  };

  const onDriversClicked = async (args?: DriversNavArgs) => {
    if (!ensureIfasModuleAccess("drivers")) return;
    setFleetAssignmentCaseReference(args?.caseNumber || "");
    setFleetAssignmentPurpose(args?.assignmentPurpose || "");
    setFleetAssignmentRequestReference(args?.requestReference || "");
    setFleetAssignmentRole(args?.allocationRole || "");
    setFleetAssignmentSequence(args?.allocationSequence ?? null);
    setFleetFocusDriverNumber(args?.focusDriverNumber || "");
    setClaimType("");
    setViewMode("DRIVERS");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
  };

  const onVehiclesClicked = async () => {
    if (!ensureIfasModuleAccess("vehicles")) return;
    setClaimType("");
    setViewMode("VEHICLES");
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
  };

  const onFleetClicked = async () => {
    if (!ensureIfasModuleAccess("fleet")) return;
    setClaimType("");
    setViewMode(FLEET);
    setShowFilterIcon(!isIfasUser);
    setShowSearchInput(!isIfasUser);
  };

  const handleCaseClick = async (caseItem: any) => {
    const { setSelectedCase } = useCaseStore.getState();

    // Save selected case globally
    setSelectedCase(caseItem);

    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getCaseDetails(caseItem.caseNumber);

    if (res.status === 200) {
      useCaseStore.getState().setSelectedCase(res.data);
      setIsChecked(res.data?.isVerified || false);
      // Case details should not show the global Claims filter/search. IFAS uses search/filter only on Case Portal list.
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(CASE_DETAILS);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleBodyRemovalRowClick = async (
    caseItem: any,
    bodyRemovalNumber: any,
  ) => {
    const { setSelectedBodyRemoval } = useBodyRemovalsStore.getState();
    const { setSelectedCase } = useCaseStore.getState();

    // Save selected case globally
    setSelectedBodyRemoval(caseItem);

    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getBodyRemovalDetails(bodyRemovalNumber);

    if (res.status === 200) {
      useBodyRemovalsStore.getState().setSelectedBodyRemoval(res.data);
      const linkedCaseNumber = res.data?.caseNumber || caseItem?.caseNumber;
      if (linkedCaseNumber) {
        const caseRes = await getCaseDetails(linkedCaseNumber);
        if (caseRes?.status === 200) {
          setSelectedCase(caseRes.data);
        }
      }
      // Body Removal list is handled via its own view; hide global search/filter for IFAS pages.
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(BODY_REMOVAL);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleQuotationClick = async (quotation: any) => {
    const { setSelectedQuotation } = useQuotationStore.getState();

    // Save selected case globally
    setSelectedQuotation(quotation);

    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getQuotationDetails(quotation.quotationNumber);

    if (res.status === 200) {
      useQuotationStore.getState().setSelectedQuotation(res.data);
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(QUOTATIONS_DETAILS);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleCateringRowClick = async (cateringId: any) => {
    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getCateringDetails(cateringId);

    if (res.status === 200) {
      useCateringStore.getState().setSelectedCatering(res.data);
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(CATERING_DETAILS);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleTombstoneRowClick = async (tombstoneId: any) => {
    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getTombstoneDetails(tombstoneId);

    if (res?.status === 200) {
      useTombstoneStore.getState().setSelectedTombstone(res.data);
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(TOMBSTONE_DETAILS);
    } else {
      showInfoToast(res?.data?.message || "Failed to load tombstone details");
    }

    setIsLoading(false);
  };

  const refreshSelectedCatering = async (cateringId: string) => {
    const refreshed = await getCateringDetails(cateringId);

    if (refreshed?.status === 200) {
      useCateringStore.getState().setSelectedCatering(refreshed.data);
    }
  };

  const refreshSelectedTombstone = async (tombstoneId: string) => {
    const refreshed = await getTombstoneDetails(tombstoneId);

    if (refreshed?.status === 200) {
      useTombstoneStore.getState().setSelectedTombstone(refreshed.data);
    }
  };

  const handleCompleteCateringDetails = async () => {
    const selectedCatering = useCateringStore.getState().selectedCatering;

    if (!selectedCatering?.cateringId) {
      showInfoToast("Invalid catering details");
      return;
    }

    const payload = {
      cateringRequired:
        selectedCatering.cateringSetup?.cateringRequired ?? true,
      guestCount: selectedCatering.cateringSetup?.guestCount ?? 0,
      servingStations: selectedCatering.cateringSetup?.servingStations ?? 2,
      menuCategoryId: selectedCatering.cateringSetup?.menuCategoryId ?? "",
      menuCategoryName:
        selectedCatering.menu?.menuCategoryDisplayName ||
        selectedCatering.cateringSetup?.menuCategoryName ||
        "",
      servingTime: selectedCatering.cateringSetup?.servingTime ?? "",
      servingStyle: selectedCatering.cateringSetup?.servingStyle ?? "",
      setupTime: selectedCatering.cateringSetup?.setupTime ?? "",
      breakdownTime: selectedCatering.cateringSetup?.breakdownTime ?? "",
      customRequests: selectedCatering.cateringSetup?.customRequests ?? "",
      menuItems: (selectedCatering.menu?.items || []).map((item) => ({
        id: item.id || undefined,
        templateItemId: item.templateItemId || undefined,
        itemName: item.itemName || "",
        quantity: item.quantity ?? 0,
        unit: item.unit || "",
        notes: item.notes ?? "",
      })),
      setupLocation: selectedCatering.logistics?.setupLocation ?? "",
      indoorOutdoor: selectedCatering.logistics?.indoorOutdoor ?? "",
      tablesRequired: selectedCatering.logistics?.tablesRequired ?? 0,
      urnsRequired: selectedCatering.logistics?.urnsRequired ?? 0,
      chafingDishesRequired:
        selectedCatering.logistics?.chafingDishesRequired ?? 0,
      tentsRequired: selectedCatering.logistics?.tentsRequired ?? false,
      accessNotes: selectedCatering.logistics?.accessNotes ?? "",
      inventoryStatus: selectedCatering.logistics?.inventoryStatus ?? "",
      supplierAssigned: selectedCatering.logistics?.supplierAssigned ?? "",
      deliveryTime: selectedCatering.logistics?.deliveryTime ?? "",
      serversRequired: selectedCatering.staffing?.serversRequired ?? 0,
      supervisorsRequired: selectedCatering.staffing?.supervisorsRequired ?? 0,
      driversRequired: selectedCatering.staffing?.driversRequired ?? 0,
      staffArrivalTime: selectedCatering.staffing?.staffArrivalTime ?? "",
      costPerHead: selectedCatering.financials?.costPerHead ?? 0,
      actualCost: selectedCatering.financials?.actualCost ?? 0,
      billingStatus: selectedCatering.financials?.billingStatus ?? "",
      internalNotes: selectedCatering.financials?.internalNotes ?? "",
      clientNotes: selectedCatering.financials?.clientNotes ?? "",
    };

    setIsSavingCatering(true);
    try {
      const res = await updateCateringDetails(
        selectedCatering.cateringId,
        userId || userIdData,
        payload,
      );

      if (res?.status === 200) {
        await refreshSelectedCatering(selectedCatering.cateringId);
        toast.success("Catering details updated successfully!");
      } else {
        showInfoToast(res?.data?.message || "Update failed");
      }
    } catch (error) {
      showInfoToast("An error occurred while updating catering details");
    } finally {
      setIsSavingCatering(false);
    }
  };

  const handleConfirmCateringDetails = async () => {
    const selectedCatering = useCateringStore.getState().selectedCatering;

    if (!selectedCatering?.cateringId) {
      showInfoToast("Invalid catering details");
      return;
    }

    setIsConfirmingCatering(true);
    try {
      const res = await confirmCateringDetails(
        selectedCatering.cateringId,
        userId || userIdData,
      );

      if (res?.status === 200) {
        await refreshSelectedCatering(selectedCatering.cateringId);
        toast.success("Catering job confirmed successfully!");
      } else {
        showInfoToast(res?.data?.message || "Confirmation failed");
      }
    } catch (error) {
      showInfoToast("An error occurred while confirming catering job");
    } finally {
      setIsConfirmingCatering(false);
    }
  };

  const handleCompleteTombstoneDetails = async (payload: any) => {
    const selectedTombstone = useTombstoneStore.getState().selectedTombstone;
    const tombstoneId = getTombstoneId(selectedTombstone);

    if (!tombstoneId) {
      showInfoToast("Invalid tombstone details");
      return;
    }

    setIsSavingTombstone(true);
    try {
      const res = await updateTombstoneDetails(
        tombstoneId,
        userId || userIdData,
        payload,
      );

      if (res?.status === 200) {
        await refreshSelectedTombstone(tombstoneId);
        toast.success("Tombstone details updated successfully!");
      } else {
        showInfoToast(res?.data?.message || "Update failed");
      }
    } catch (error) {
      showInfoToast("An error occurred while updating tombstone details");
    } finally {
      setIsSavingTombstone(false);
    }
  };

  const handleConfirmTombstoneDetails = async () => {
    const selectedTombstone = useTombstoneStore.getState().selectedTombstone;
    const tombstoneId = getTombstoneId(selectedTombstone);

    if (!tombstoneId) {
      showInfoToast("Invalid tombstone details");
      return;
    }

    setIsConfirmingTombstone(true);
    try {
      const res = await confirmTombstoneDetails(
        tombstoneId,
        userId || userIdData,
      );

      if (res?.status === 200) {
        await refreshSelectedTombstone(tombstoneId);
        toast.success("Tombstone job confirmed successfully!");
      } else {
        showInfoToast(res?.data?.message || "Confirmation failed");
      }
    } catch (error) {
      showInfoToast("An error occurred while confirming tombstone job");
    } finally {
      setIsConfirmingTombstone(false);
    }
  };

  const handleDriverListClick = async (driverId: any) => {
    const { setSelectedDriver } = useDriverStore.getState();

    // Save selected case globally
    setSelectedDriver(driverId);

    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getDriverDetails(driverId);

    if (res.status === 200) {
      useDriverStore.getState().setSelectedDriver(res.data);
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(DRIVERS_DETAILS);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleVehicleListClick = async (vehicleID: any) => {
    const { setSelectedVehicle } = useVehicleStore.getState();

    // Save selected case globally
    setSelectedVehicle(vehicleID);

    setClaimBenefitData(null);
    setIsEditable(false);
    setIsLoading(true);
    setIsClaimAssigned(false);

    const res = await getVehicleDetails(vehicleID);

    if (res.status === 200) {
      useVehicleStore.getState().setSelectedVehicle(res.data);
      setShowFilterIcon(!isIfasUser);
      setShowSearchInput(!isIfasUser);
      setViewMode(VEHICLE_DETAILS);
    } else {
      showInfoToast(res.data.message);
    }

    setIsLoading(false);
  };

  const handleNewCaseCLick = () => {
    setIsEditable(false);
    setViewMode(CREATE_CASE);
    setShowCollapse(false);
  };

  const handleNewQuotationClick = () => {
    setIsEditable(false);
    setViewMode(CREATE_QUOTATION);
    setShowCollapse(false);
  };

  const handleAddDriverClick = () => {
    setIsEditable(false);
    setViewMode(ADD_DRIVER);
    setShowCollapse(false);
  };

  const handleAddVehicleClick = () => {
    setIsEditable(false);
    setViewMode(ADD_VEHICLE);
    setShowCollapse(false);
  };

  const applyIfasCaseFilters = async ({
    status,
    searchText: nextSearchText,
    branchName,
    page: nextPage,
  }: {
    status: string | null;
    searchText: string;
    branchName?: string | null;
    page: number;
  }) => {
    setIsLoading(true);
    setCaseSelectedStatus(status);
    setSearchText(nextSearchText);

    const response = await searchCases({
      status: status || undefined,
      searchText: nextSearchText || undefined,
      branchName: branchName || undefined,
      page: nextPage,
      size: PAGE_COUNT,
    });

    if (response?.status === 200) {
      useCaseStore.getState().setCases(response.data);
      setCasesTotalCount(response.data?.totalCount || 0);
    } else {
      showInfoToast(response?.data?.message || "Failed to load cases");
    }

    setIsLoading(false);
  };

  const applyIfasBodyRemovalFilters = async ({
    status,
    searchText: nextSearchText,
    page: nextPage,
  }: {
    status: string | null;
    searchText: string;
    page: number;
  }) => {
    setIsLoading(true);
    setBodyRemovalSelectedStatus(status);
    setBodyRemovalSearchText(nextSearchText);

    const response = await searchBodyRemovals({
      status: status || undefined,
      searchText: nextSearchText || undefined,
      page: isIfasDriver ? 0 : nextPage,
      size: isIfasDriver ? 1000 : PAGE_COUNT,
    });

    if (response?.status === 200) {
      useBodyRemovalsStore.getState().setBodyRemovals(response.data);
    } else {
      showInfoToast(response?.data?.message || "Failed to load body removals");
    }

    setIsLoading(false);
  };

  /**
   * The Case Portal search.
   *
   * The shell owns the header search box, the status filter menu and the pager,
   * so it decides *when* to search; this owns *how*. Before the split these
   * twelve lines were inlined at all three of those call sites.
   */
  const searchIfasCases = async ({
    status,
    searchText: nextSearchText,
    page: nextPage,
  }: {
    status: string | null;
    searchText: string;
    page: number;
  }) => {
    setIsLoading(true);

    const response = await searchCases({
      status,
      searchText: nextSearchText,
      page: nextPage,
      size: PAGE_COUNT,
    });

    if (response?.status === 200) {
      useCaseStore.getState().setCases(response.data);
      setCasesTotalCount(response.data?.totalCount || 0);
    } else {
      showInfoToast(response?.data?.message || "Failed to load cases");
    }

    setIsLoading(false);
  };

  /**
   * The IFAS view for `viewMode`, or null when this is a Claims view.
   *
   * Returning null rather than throwing is what lets the shell keep its own
   * switch: it renders the claims views and falls through to this one, so
   * neither half needs to know the other's case labels.
   */
  const renderView = (viewMode: string): React.ReactNode => {
    switch (viewMode) {
      case "CREATE_CASE":
        return (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Box p={3} bgcolor="background.paper">
              <CreateCase onSubmit={handleOnCaseSubmit} />
            </Box>
          </Box>
        );

      case "CREATE_QUOTATION":
        return (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Box p={3} bgcolor="background.paper">
              <CreateQuotation onSubmit={handleOnQuotationSubmit} />
            </Box>
          </Box>
        );

      case "REVENUE":
        return <RevenuePortal />;

      case "CATALOGUE":
        return <CataloguePortal />;

      case "ADD_DRIVER":
        return (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Box p={3} bgcolor="background.paper">
              <AddDriver onSubmit={handleOnDriverSubmit} />
            </Box>
          </Box>
        );
      case "ADD_VEHICLE":
        return (
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <Box p={3} bgcolor="background.paper">
              <AddVehicle onSubmit={handleOnVehicleSubmit} />
            </Box>
          </Box>
        );

      case "IFAS_HOME":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <IFASHome
              isDriver={isIfasDriver}
              role={effectiveRole}
              onCasePortalClicked={onCasePortalClicked}
              onFuneralPlanningClicked={onFuneralPlanningClicked}
              onSpecialServicesClicked={onSpecialServicesClicked}
              onBodyRemovalClicked={onBodyRemovalClicked}
              onMortuaryClicked={onMortuaryClicked}
              onWarehouseClicked={onWarehouseClicked}
              onBodyDispatchClicked={onBodyDispatchClicked}
              onQuotationClicked={onQuotationClicked}
              onRevenueClicked={() => setViewMode("REVENUE")}
              onMemorialsPortalClicked={onMemorialsPortalClicked}
              onTombstonePortalClicked={onTombstonePortalClicked}
              onCateringPortalClicked={onCateringPortalClicked}
              onFleetClicked={onFleetClicked}
              onDriversClicked={onDriversClicked}
              onIfasReportsClicked={onIfasReportsClicked}
            />
          </Box>
        );

      case "IFAS_REPORTS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <IFASReportingHub />
          </Box>
        );

      case "FLEET":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <FleetPortal
              onDriversClicked={onDriversClicked}
              onVehiclesClicked={onVehiclesClicked}
            />
          </Box>
        );

      case "CASE-PORTAL":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {/* <CasePortal onSelectCase={handleCaseClick} isCasePortal={isCasePortal}/> */}
            <CasePortal
              title="Case Portal"
              recentFilter="Recent Cases"
              fetchCases={getAllCases}
              onSelectCase={handleCaseClick}
              showDateOfDeath={true}
              totalCasesCount={casesTotalCount}
              currentPage={page}
              rowsPerPage={pageSize}
              setCurrentPage={setPage}
              setClaimsTotalCount={setCasesTotalCount}
              onCaseHistoryClicked={onCaseHistoryClicked}
              isIfasUser={isIfasUser}
              isIfasCasePortal={isIfasUser}
              applyIfasCaseFilters={applyIfasCaseFilters}
              selectedIfasStatus={caseSelectedStatus}
              selectedIfasSearchText={searchText}
            />
          </Box>
        );
      case "FUNERAL_PLANNING":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <FuneralPlanningPortal />
          </Box>
        );
      case "SPECIAL_SERVICES":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <SpecialServicesPortal />
          </Box>
        );
      case "MEMORIALS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <MemorialsPortal />
          </Box>
        );
      case "BODY-REMOVAL":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {/* <CasePortal onSelectCase={handleCaseClick} isCasePortal={isCasePortal}/> */}
            <CasePortal
              title="Body Removal"
              recentFilter="Recent Body Removal"
              fetchBodyRemovals={getAllBodyRemovals}
              onSelectBodyRemoval={handleBodyRemovalRowClick}
              showDateOfDeath={false}
              currentPage={page}
              rowsPerPage={pageSize}
              setCurrentPage={setPage}
              isBodyRemoval={true}
              driverOnly={isIfasDriver}
              isIfasBodyRemovalPortal={isIfasUser}
              applyIfasBodyRemovalFilters={applyIfasBodyRemovalFilters}
              selectedIfasBodyRemovalStatus={bodyRemovalSelectedStatus}
              selectedIfasBodyRemovalSearchText={bodyRemovalSearchText}
            />
          </Box>
        );

      case "MORTUARY":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <MortuaryPortal onSelectMortuaryRecord={handleMortuaryRecordClick} />
          </Box>
        );

      case "WAREHOUSE":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <WarehousePortal />
          </Box>
        );

      case "MORTUARY_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <MortuaryPortal
              mortuaryRecordId={selectedMortuaryRecordId}
              onBack={resetToMortuaryList}
              onBodyDispatchClicked={onBodyDispatchClicked}
            />
          </Box>
        );

      case "BODY_DISPATCH":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <BodyDispatchPortal
              onMortuaryClicked={onMortuaryClicked}
              onSelectDispatch={handleBodyDispatchClick}
            />
          </Box>
        );

      case "BODY_DISPATCH_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <BodyDispatchPortal
              dispatchNumber={selectedBodyDispatchNumber}
              onBack={resetToBodyDispatchList}
              onMortuaryClicked={onMortuaryClicked}
            />
          </Box>
        );

      case "CATERING-PORTAL":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <CateringPortal
              fetchCatering={getAllCatering}
              currentPage={page}
              rowsPerPage={pageSize}
              setCurrentPage={setPage}
              appliedSearchText={cateringSearchText}
              selectedStatus={selectedStatus}
              onSelectedCatering={handleCateringRowClick}
            />
          </Box>
        );

      case "TOMBSTONE-PORTAL":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <TombstonePortal
              fetchTombstones={getAllTombstones}
              currentPage={page}
              rowsPerPage={pageSize}
              setCurrentPage={setPage}
              onSelectTombstone={handleTombstoneRowClick}
            />
          </Box>
        );

      case "QUOTATIONS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {/* <CasePortal onSelectCase={handleCaseClick} isCasePortal={isCasePortal}/> */}
            <CasePortal
              title="Quotations"
              recentFilter="Recent Quotations"
              fetchQuotations={getAllQuotations}
              onSelectQuotation={handleQuotationClick}
              showDateOfDeath={false}
              currentPage={page}
              rowsPerPage={pageSize}
              setCurrentPage={setPage}
              //isBodyRemoval={true}
              isQuotation={true}
            />
          </Box>
        );

      case "DRIVERS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <Drivers
              title="Drivers"
              recentFilter="Driver List"
              onSelectDriver={handleDriverListClick}
              showDateOfDeath={false}
              fleetType="driver"
              driverOnly={isIfasDriver}
              initialCaseReference={fleetAssignmentCaseReference}
              initialAssignmentPurpose={fleetAssignmentPurpose}
              initialRequestReference={fleetAssignmentRequestReference}
              initialAllocationRole={fleetAssignmentRole}
              initialAllocationSequence={fleetAssignmentSequence}
              focusDriverNumber={fleetFocusDriverNumber}
              onAddDriverClick={handleAddDriverClick}
              onAssignmentCompleted={() => {
                setFleetAssignmentCaseReference("");
                setFleetAssignmentPurpose("");
                setFleetAssignmentRequestReference("");
                setFleetAssignmentRole("");
                setFleetAssignmentSequence(null);
                setFleetFocusDriverNumber("");
                setViewMode("FLEET");
              }}
            //isBodyRemoval={true}
            />
          </Box>
        );

      case "DRIVERS_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <DriverDetails
              onHistoryClick={onDriverHistoryClicked}
              readOnly={isIfasDriver}
            />
          </Box>
        );

      case "VEHICLES":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <Vehicles
              title="Vehicles"
              recentFilter="Vehicle List"
              onSelectVehicle={handleVehicleListClick}
              showDateOfDeath={false}
              fleetType="vehicle"
              onAddVehicleClick={handleAddVehicleClick}
            //isBodyRemoval={true}
            />
          </Box>
        );

      case "VEHICLE_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <VehicleDetails onHistoryClick={onVehicleHistoryClicked} />
          </Box>
        );

      case "CASE_DETAILS":
      case "BODY_REMOVAL":
      case "QUOTATIONS_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <CaseDetails
              viewMode={viewMode}
              refreshBodyRemovals={handleBodyRemovalRowClick}
              onCasePortalClicked={onCasePortalClicked}
              onCaseHistoryClicked={onCaseHistoryClicked}
              onBodyRemovalHistoryClicked={onBodyRemovalHistoryClicked}
              resetToCaseList={resetToCaseList}
              resetToBodyRemovalList={resetToBodyRemovalList}
            />
          </Box>
        );

      case "CATERING_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <CateringDetails
              onComplete={handleCompleteCateringDetails}
              isSaving={isSavingCatering}
              onConfirm={handleConfirmCateringDetails}
              isConfirming={isConfirmingCatering}
              onHistoryClick={onCateringHistoryClicked}
            />
          </Box>
        );

      case "TOMBSTONE_DETAILS":
        return (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <TombstoneDetails
              onUpdate={handleCompleteTombstoneDetails}
              isSaving={isSavingTombstone}
              onConfirm={handleConfirmTombstoneDetails}
              isConfirming={isConfirmingTombstone}
              onBackToList={onTombstonePortalClicked}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return {
    // State the shell reads back (the filter menu shows the selected status).
    caseSelectedStatus,
    setCaseSelectedStatus,

    // Navigation, for the Sidebar.
    onCasePortalClicked,
    onBodyRemovalClicked,
    onIfasReportsClicked,
    onFuneralPlanningClicked,
    onSpecialServicesClicked,
    onMemorialsPortalClicked,
    onCateringPortalClicked,
    onTombstonePortalClicked,
    onQuotationClicked,
    onMortuaryClicked,
    onWarehouseClicked,
    onBodyDispatchClicked,
    onDriversClicked,
    onVehiclesClicked,
    onFleetClicked,

    // Creation entry points, for the SearchBar.
    handleNewCaseCLick,
    handleNewQuotationClick,
    handleAddDriverClick,
    handleAddVehicleClick,

    // Deep links: the shell's location-state effect can land on a case.
    handleCaseClick,

    searchIfasCases,
    renderView,
  };
}
